
import { useState } from 'react';
import { AlertTriangle, Upload, X } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useToast } from '../../hooks/use-toast';
import { authorizedFetch } from '../../auth/keycloakProvider';
import { useKeycloak } from '../../auth/keycloakProvider';
import { getErrorToast } from '../../lib/error-utils';
import { TransportNote, NoteSeverity, NoteType  } from '../../types';

interface TransportNoteModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (note: Omit<TransportNote, 'id'>) => void;
    companyName: string;
    documentUUID: string;
}

const noteTypeLabels: Record<NoteType, string> = {
    reservation: 'Reserva',
    observation: 'Observação',
    damage: 'Dano',
    delay: 'Atraso',
    other: 'Outro',
};

const severityLabels: Record<NoteSeverity, string> = {
    low: 'Baixa',
    medium: 'Média',
    high: 'Alta',
    critical: 'Crítica',
};

const severityColors: Record<NoteSeverity, string> = {
    low: 'text-status-delivered-foreground',
    medium: 'text-status-pending-foreground',
    high: 'text-status-in_transit-foreground',
    critical: 'text-destructive',
};

// Capitalize first letter to match backend enum string values (e.g. 'observation' → 'Observation')
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function TransportNoteModal({
    open,
    onOpenChange,
    onSubmit,
    companyName,
    documentUUID,
}: TransportNoteModalProps) {
    const { toast } = useToast();
    const { user } = useKeycloak();
    const [noteType, setNoteType] = useState<NoteType>('observation');
    const [severity, setSeverity] = useState<NoteSeverity>('low');
    const [location, setLocation] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState<string | undefined>();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [resolved, setResolved] = useState(false);
    const [resolution, setResolution] = useState('');

    const shouldNotifyCarrier = severity === 'high' || severity === 'critical';

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setImageUrl(undefined);
        setSelectedFile(null);
    };

    const resetForm = () => {
        setNoteType('observation');
        setSeverity('low');
        setLocation('');
        setDescription('');
        setImageUrl(undefined);
        setSelectedFile(null);
        setResolved(false);
        setResolution('');
    };

    const handleSubmit = async () => {
        if (!location.trim()) {
            toast({
                title: 'Erro',
                description: 'A localização é obrigatória.',
                variant: 'destructive',
            });
            return;
        }

        if (resolved && !resolution.trim()) {
            toast({
                title: 'Erro',
                description: 'A resolução é obrigatória quando a nota está marcada como resolvida.',
                variant: 'destructive',
            });
            return;
        }

        const note: Omit<TransportNote, 'id'> = {
            type: noteType,
            severity,
            location,
            description,
            imageUrl,
            resolved,
            resolution: resolved ? resolution : undefined,
            reportedBy: user?.name ?? '',
            reportedAt: new Date(),
        };

        try {
            // Build multipart/form-data — do NOT set Content-Type manually so the
            // browser can append the correct boundary for the multipart body.
            const formData = new FormData();
            formData.append('documentUUID', documentUUID);
            formData.append('noteType', capitalize(noteType));
            formData.append('severity', capitalize(severity));
            formData.append('description', description);
            formData.append('location', location);
            if (selectedFile) {
                formData.append('file', selectedFile);
            }

            const response = await authorizedFetch('/api/deliveries/notes', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Falha ao adicionar nota de transporte');
            }

            onSubmit(note);
            resetForm();
            onOpenChange(false);
        } catch (err) {
            const errorToast = getErrorToast(err, 'Erro', 'Falha ao adicionar nota de transporte');
            toast({
                title: errorToast.title,
                description: errorToast.description,
                variant: 'destructive',
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Adicionar Nota de Transporte</DialogTitle>
                    <DialogDescription>
                        Registe uma ocorrência durante o transporte. Notas com severidade superior a média notificam o transportador.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Note Type */}
                    <div className="space-y-2">
                        <Label htmlFor="noteType">Tipo de Nota</Label>
                        <Select value={noteType} onValueChange={(v) => setNoteType(v as NoteType)}>
                            <SelectTrigger>
                                <SelectValue placeholder="Selecione o tipo" />
                            </SelectTrigger>
                            <SelectContent>
                                {(Object.keys(noteTypeLabels) as NoteType[]).map((type) => (
                                    <SelectItem key={type} value={type}>
                                        {noteTypeLabels[type]}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Severity */}
                    <div className="space-y-2">
                        <Label htmlFor="severity">Severidade</Label>
                        <Select value={severity} onValueChange={(v) => setSeverity(v as NoteSeverity)}>
                            <SelectTrigger>
                                <SelectValue placeholder="Selecione a severidade" />
                            </SelectTrigger>
                            <SelectContent>
                                {(Object.keys(severityLabels) as NoteSeverity[]).map((sev) => (
                                    <SelectItem key={sev} value={sev}>
                                        <span className={severityColors[sev]}>{severityLabels[sev]}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Carrier notification alert */}
                    {shouldNotifyCarrier && (
                        <Alert variant="default" className="border-status-pending bg-status-pending-bg">
                            <AlertTriangle className="h-4 w-4 text-status-pending-foreground" />
                            <AlertDescription className="text-status-pending-foreground">
                                O transportador <strong>{companyName}</strong> será notificado sobre esta ocorrência.
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Location */}
                    <div className="space-y-2">
                        <Label htmlFor="location">Localização *</Label>
                        <Input
                            id="location"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            placeholder="Ex: Armazém de Lisboa, KM 45 da A1"
                        />
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Label htmlFor="description">Descrição</Label>
                        <Textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Descreva a ocorrência em detalhe..."
                            rows={3}
                        />
                    </div>

                    {/* Image Upload */}
                    <div className="space-y-2">
                        <Label>Imagem (opcional)</Label>
                        {imageUrl ? (
                            <div className="relative">
                                <img
                                    src={imageUrl}
                                    alt="Preview"
                                    className="w-full h-40 object-cover rounded-lg border"
                                />
                                <Button
                                    variant="destructive"
                                    size="icon"
                                    className="absolute top-2 right-2 h-8 w-8"
                                    onClick={handleRemoveImage}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center w-full">
                                <label
                                    htmlFor="image-upload"
                                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                                >
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                                        <p className="text-sm text-muted-foreground">
                                            Clique para carregar uma imagem
                                        </p>
                                    </div>
                                    <input
                                        id="image-upload"
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                    />
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Reported By — auto-populated from the logged-in user */}
                    <div className="space-y-2">
                        <Label htmlFor="reportedBy">Reportado Por</Label>
                        <Input
                            id="reportedBy"
                            value={user?.name ?? 'A carregar...'}
                            disabled
                            className="bg-muted"
                        />
                        <p className="text-xs text-muted-foreground">Preenchido automaticamente com a sua conta</p>
                    </div>

                    {/* Resolved */}
                    <div className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                            <Label htmlFor="resolved">Resolvido</Label>
                            <p className="text-sm text-muted-foreground">
                                Marque se a ocorrência já foi resolvida
                            </p>
                        </div>
                        <Switch
                            id="resolved"
                            checked={resolved}
                            onCheckedChange={setResolved}
                        />
                    </div>

                    {/* Resolution (only if resolved) */}
                    {resolved && (
                        <div className="space-y-2">
                            <Label htmlFor="resolution">Resolução *</Label>
                            <Textarea
                                id="resolution"
                                value={resolution}
                                onChange={(e) => setResolution(e.target.value)}
                                placeholder="Descreva como a ocorrência foi resolvida..."
                                rows={2}
                            />
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSubmit}>
                        Adicionar Nota
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}